import React, { useEffect, useState } from "react";

import { Button } from "@app/scripts/react-general-components/Button";
import { Drawer } from "@app/scripts/react-general-components/Drawer";
import { useShopify, useShopifyProduct } from "@app/scripts/react-hooks/shopify";
import { shopify } from "@app/scripts/utils/graphql";
import { GET_PRODUCTS_BY_FILTER } from "@app/scripts/utils/queries";
import { ProductMoreColorsProduct } from "@app/scripts/react-product-more-colors/components/ProductMoreColorsProduct";

import type { NormalisedProduct, Product } from "@app/scripts/types";
import { isUS } from "@app/scripts/utils";

type Props = {
  product: Product;
};

export const ProductMoreColorsContent = ({ product }: { product: NormalisedProduct }) => {
  const { normaliseProduct } = useShopify();
  const { color, title: productTitle } = useShopifyProduct({ product });
  const [related, setRelated] = useState<NormalisedProduct[]>([]);
  const [loaded, setLoaded] = useState(false);

  const colorString = isUS ? "color" : "colour";

  const title = `${productTitle}: ${related?.length} ${colorString}s`;
  const btnTitle = `View all ${related?.length} ${colorString}s`;

  const cropFromTop = /short|pant|hat/.test(product.productType?.toLowerCase() || "");

  useEffect(() => {
    const query = async () => {
      const styleTag = product.tags.find((tag) => tag.includes("style--"));
      if (!styleTag) return;

      const data = await shopify(GET_PRODUCTS_BY_FILTER, {
        filter: "tag:" + styleTag,
        limit: 100,
      });

      setRelated(data?.products?.nodes?.map((p) => normaliseProduct(p) || []));
      setLoaded(true);
    };
    query();
  }, []);

  const items = related?.filter((item) => !item.id.includes(product.id));
  const selectedOne = related?.filter((item) => item.id.includes(product.id));
  
  // Sort items by number of available variants (most to least), maintaining original order for ties
  const sortedItems = items.sort((a, b) => {
    const aAvailableVariantCount = a.variants?.filter(variant => variant.available).length || 0;
    const bAvailableVariantCount = b.variants?.filter(variant => variant.available).length || 0;
    return bAvailableVariantCount - aAvailableVariantCount;
  });
  
  const sortedRelated = [...selectedOne, ...sortedItems];

  return (
    <>
      <div className="flex justify-between pb-4 items-center">
        <span className="text-lg product__color-tag">
          <strong>{colorString}: </strong> {color}
        </span>

        {loaded && related?.length > 1 && (
          <Drawer
            heading={title}
            opensFrom={"bottom"}
            drawerClassName="product-more-colors__drawer"
            customButton={({ toggleOpen }) => (
              <Button
                variant="text"
                className="swiper-no-swiping product-more-colors__button min-h-0"
                ariaLabel={btnTitle}
                onClick={toggleOpen}
                label={btnTitle}
              />
            )}
          >
            <div className="product-more-colors page-width">
              {sortedRelated?.map((r_product) => (
                <ProductMoreColorsProduct key={r_product.id} product={r_product} parentProduct={product} />
              ))}
            </div>
          </Drawer>
        )}
      </div>

      {!loaded && (
        <div className="product__related">
          <item-carousel
            columns-desktop="6"
            columns-mobile="4"
            disabled-mobile="false"
            disabled-desktop="false"
            scrollbar-mobile="true"
            small-btns="true"
          >
            <div className={`item-carousel product__related_item-carousel ${cropFromTop ? "crop-from-top" : ""}`}>
              {Array.apply(null, Array(10)).map((_) => (
                <div className="product__related_item-carousel__image" />
              ))}
            </div>
          </item-carousel>
        </div>
      )}

      {loaded && related?.length > 0 && (
        <div className="product__related">
          <item-carousel
            columns-desktop="6"
            columns-mobile="4"
            disabled-mobile="false"
            disabled-desktop="false"
            scrollbar-mobile="true"
            small-btns="true"
          >
            <div className="item-carousel product__related_item-carousel">
              <a href={`/products/${product.handle}`} key={product.id} className={"image-container active-border"}>
                {product.media?.[0]?.src ? (
                  <img
                    srcSet={`${product.media?.[0].src}&width=70 70w,
                    ${product.media?.[0].src}&width=150 150w,
                    ${product.media?.[0].src}&width=202 202w`}
                    src={`${product.media?.[0].src}&width=70}`}
                    alt={product.media?.[0].alt}
                    className="product__related_item-carousel__image"
                    loading="lazy"
                  />
                ) : (
                  <div className="product__related_item-carousel__image" />
                )}
              </a>
              {sortedItems.map((r_product) => {
                const isCurrentProduct = r_product.id.includes(product.id);
                return !isCurrentProduct ? (
                  <a
                    href={`/products/${r_product.handle}`}
                    key={r_product.id}
                    className={`image-container ${isCurrentProduct ? "active-border" : ""}`}
                  >
                    {r_product.media?.[0]?.src ? (
                      <img
                        srcSet={`${r_product.media?.[0].src}&width=70 70w,
                        ${r_product.media?.[0].src}&width=150 150w,
                        ${r_product.media?.[0].src}&width=202 202w`}
                        src={`${r_product.media?.[0].src}&width=70}`}
                        alt={r_product.media?.[0].alt}
                        className="product__related_item-carousel__image"
                        loading="lazy"
                      />
                    ) : (
                      <div className="product__related_item-carousel__image" />
                    )}
                  </a>
                ) : (
                  ""
                );
              })}
            </div>
          </item-carousel>
        </div>
      )}
    </>
  );
};

export const ProductMoreColors: React.FC<Props> = ({ product }) => {
  const { normaliseProduct } = useShopify();
  const normalised = normaliseProduct(product);
  return <ProductMoreColorsContent product={normalised} />;
};
